import React, { useRef, useState } from "react";
import { Platform, Pressable, StyleSheet, Text, View, Animated } from "react-native";
import CustomText from "../../components/common/general/CustomText/CustomText";
import { colors } from "../../styles/design-system";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { Circle, CircleCheck } from "lucide-react";
import { FormattedObjective } from "../../graphql/generated/graphql";
import useResponsiveScreen from "../../hooks/useResponsiveScreen";

export interface Objective extends FormattedObjective {
  isCompleted?: boolean;
}

interface LearningGoalsContainerProps {
  objectives: Objective[];
}

const EXPANDED_WIDTH = 280;
const COLLAPSED_WIDTH = 56;

const LearningGoalsContainer: React.FC<LearningGoalsContainerProps> = ({ objectives }) => {
  const { isLarge, isXXLarge } = useResponsiveScreen();

  const [menuVisible, setMenuVisible] = useState(isXXLarge);
  const [objectiveHoveredId, setObjectiveHoveredId] = useState(null);
  const [selectedObjectiveId, setSelectedObjectiveId] = useState(null);

  const animatedWidth = useRef(new Animated.Value(isXXLarge ? EXPANDED_WIDTH : COLLAPSED_WIDTH)).current; // Start at the default position

  const completeObjectivesCount = objectives.filter((objective) => objective.isCompleted).length;

  const handleExpand = () => {
    if (menuVisible) {
      setMenuVisible(false);
      Animated.timing(animatedWidth, {
        toValue: COLLAPSED_WIDTH,
        duration: 300,
        useNativeDriver: false,
      }).start();
    } else {
      setMenuVisible(true);
      Animated.timing(animatedWidth, {
        toValue: EXPANDED_WIDTH,
        duration: 300,
        useNativeDriver: false,
      }).start();
    }
  };

  return (
    <Animated.View
      style={[styles.learningGoalsContainer, isLarge && styles.learningGoalsContainerLarge, { width: animatedWidth }]}>
      {/* Header */}
      {menuVisible ? (
        <View style={styles.header}>
          <CustomText useTranslationText={false} text="Learning Goals" style={styles.title} weight="bold" size="s" />
          <CustomText
            text="Goals Count"
            interpolation={{ completedCount: completeObjectivesCount, total: objectives.length }}
            style={styles.subTitle}
          />

          <View style={styles.minimizeButtonContainer}>
            <CustomButton
              leftIcon={<CaretLeft size={16} />}
              text=""
              styleType="secondaryLightened"
              style={styles.minimizeButton}
              onPress={handleExpand}
            />
          </View>
        </View>
      ) : (
        <View style={styles.maximizeButtonContainer}>
          <CustomButton
            leftIcon={<CaretRight size={16} />}
            text=""
            styleType="secondaryLightened"
            onPress={handleExpand}
          />
        </View>
      )}

      {/* Content */}
      <View style={[menuVisible ? styles.contentExpanded : styles.contentCollapsed]}>
        {objectives.map((objective) => {
          const isSelected = selectedObjectiveId === objective.id;
          const isHovered = !objective.isCompleted && objectiveHoveredId === objective.id;
          return (
            <Pressable
              style={[
                styles.objectiveContainer,
                menuVisible ? styles.objectiveContainerExpanded : styles.objectiveContainerCollapsed,
                menuVisible && isSelected && styles.objectiveContainerActive,
              ]}
              key={objective.id}
              onMouseEnter={() => Platform.OS === "web" && setObjectiveHoveredId(objective.id)}
              onMouseLeave={() => Platform.OS === "web" && setObjectiveHoveredId(null)}
              onPress={() => setSelectedObjectiveId(objective.id)}>
              <View style={[styles.objectiveIcon, menuVisible && styles.objectiveIconFull]}>
                {objective.isCompleted ? (
                  <CircleCheck size={20} color={colors.success[600]} />
                ) : (
                  <Circle
                    size={20}
                    color={isHovered ? colors.brand[500] : menuVisible ? colors.grey[400] : colors.grey[300]}
                  />
                )}
              </View>
              {menuVisible ? (
                <Text
                  style={[
                    styles.objectiveText,
                    isHovered && styles.objectiveTextHover,
                    objective.isCompleted && styles.completedObjectiveText,
                  ]}>
                  {objective.objective}
                </Text>
              ) : null}
            </Pressable>
          );
        })}
        {!menuVisible ? (
          <CustomText
            text={`${completeObjectivesCount}/${objectives.length}`}
            style={styles.counterText}
            useTranslationText={false}
          />
        ) : null}
      </View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  learningGoalsContainer: {
    borderWidth: 1,
    borderColor: colors.grey[200],
    overflow: "hidden",
    borderRadius: 24,
    backgroundColor: "#fff",
    shadowColor: "rgba(0, 0, 0, 0.08)",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 1,
    shadowRadius: 16,
    elevation: 4,
    position: "absolute",
    top: 8,
    left: 8,
    // @ts-expect-error - works for web
    height: "fit-content",
  },
  learningGoalsContainerLarge: {
    top: 16,
    left: 16,
  },
  header: {
    position: "relative",
    paddingTop: 16,
    paddingHorizontal: 20,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  title: {
    lineHeight: 21,
    color: colors.grey[900],
  },
  subTitle: {
    fontSize: 10,
    lineHeight: 15,
    color: colors.grey[600],
  },
  minimizeButtonContainer: {
    position: "absolute",
    top: 20,
    right: 20,
    zIndex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  minimizeButton: {
    padding: 6,
  },
  maximizeButtonContainer: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 4,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  contentCollapsed: {
    paddingTop: 16,
    paddingBottom: 12,
    gap: 12,
    alignItems: "center",
  },
  contentExpanded: {
    padding: 20,
    gap: 8,
  },
  objectiveContainer: {
    flexDirection: "row",
    gap: 8,
    cursor: "pointer",
    borderRadius: 16,
  },
  objectiveContainerExpanded: {
    alignItems: "flex-start",
    padding: 6,
  },
  objectiveContainerCollapsed: {
    width: "100%",
    justifyContent: "center",
  },
  objectiveContainerActive: {
    backgroundColor: colors.brand[50],
  },
  objectiveTextHover: {
    color: colors.brand[700],
  },
  objectiveIcon: {
    borderRadius: "50%",
    alignItems: "center",
    justifyContent: "center",
  },
  objectiveIconFull: {
    marginTop: 4,
  },
  objectiveText: {
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 20,
    color: colors.grey[900],
  },
  completedObjectiveText: {
    color: colors.grey[500],
    opacity: 0.7,
    textDecorationLine: "line-through",
  },
  counterText: {
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 18,
    color: colors.grey[900],
  },
  lottie: {
    width: 24,
    height: 24,
  },
});

export default LearningGoalsContainer;
